/**
 * The shared section composition inside a PageShell.
 *
 * A section is a small hierarchy rather than a typography class: it names its
 * region, may explain it, keeps secondary actions before the final primary
 * action, and owns the spacing to its body. It deliberately knows nothing
 * about grids; GridBar remains the command surface for grid-specific acts.
 *
 * ── `eyebrow` AND `status`, because a region is a small page (#2283) ────────
 * `PageShell` has had both since #2014 and this had neither, and the gap is
 * what the Console's unconverted regions were measured against: adopting this
 * meant DROPPING content, so pages kept their hand-built heads instead.
 *
 *   · a status beside the title — the release rail's delivery badge, the setup
 *     cockpit's ready pill. Not an action: it is something the region IS.
 *   · an eyebrow above it — every one of ContextPage's five composer panels
 *     opens with a categorising line ("Explicit semantics", "Accountable
 *     handoff") above a title and a description. Folding that into the title
 *     puts it in the heading's accessible name, which is not what it is.
 *
 * Both are optional and render nothing when absent, so every existing call
 * site is byte-identical. The markup mirrors `PageShell`'s identity block
 * exactly — eyebrow, then a title line carrying the status, then the summary —
 * because a reader moving between a page and a section inside it should meet
 * the same shape at a smaller size, and two spellings of one hierarchy is how
 * the two drift.
 */
import { useId, type ReactNode } from 'react'
import s from './PageSection.module.css'
import e from './Engraved.module.css'

export interface PageSectionProps {
  title: ReactNode
  headingLevel?: 2 | 3
  /** A categorising line above the title. Not part of the accessible name. */
  eyebrow?: ReactNode
  summary?: ReactNode
  /** What this region IS — a state badge, a count pill. Never an action. */
  status?: ReactNode
  secondaryActions?: ReactNode
  primaryAction?: ReactNode
  testId?: string
  /**
   * `engraved` is a section CUT INTO A SLAB — the leading designs' "REGISTRY"
   * inside the record's Details card: the heading keeps its level and its
   * accessible name, and takes the shared engraved label instead of the page
   * title size, so a section inside a card reads as one of the card's sections
   * rather than as a new page heading. `default` is every existing call site.
   */
  appearance?: 'default' | 'engraved'
  children: ReactNode
}

export function PageSection({
  title,
  headingLevel = 2,
  eyebrow,
  summary,
  status,
  secondaryActions,
  primaryAction,
  testId,
  appearance = 'default',
  children,
}: PageSectionProps) {
  const generatedId = useId().replace(/:/g, '')
  const titleId = `page-section-title-${generatedId}`
  const Heading = `h${headingLevel}` as 'h2' | 'h3'
  const hasActions = secondaryActions != null || primaryAction != null

  return (
    <section className={s.section} aria-labelledby={titleId} data-page-section="" data-testid={testId}
      data-appearance={appearance === 'engraved' ? 'engraved' : undefined}>
      <header className={s.header}>
        <div className={s.identity}>
          {eyebrow != null && <div className={s.eyebrow}>{eyebrow}</div>}
          <div className={s.titleLine}>
            <Heading className={appearance === 'engraved' ? e.label : s.title} id={titleId}>{title}</Heading>
            {status != null && <div className={s.status}>{status}</div>}
          </div>
          {summary != null && <div className={s.summary}>{summary}</div>}
        </div>
        {hasActions && (
          <div className={s.actions} data-page-section-actions="">
            {secondaryActions}
            {primaryAction}
          </div>
        )}
      </header>
      <div className={s.body}>{children}</div>
    </section>
  )
}
